import hljs from "highlight.js";
import "highlight.js/styles/github-dark.css";
import { useState, type ComponentProps } from "react";
import { inTauri } from "@/lib/tools";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

function CodeBlock({ className, children, ...props }: ComponentProps<"code">) {
  const match = /language-(\w+)/.exec(className ?? "");
  const code = String(children).replace(/\n$/, "");
  const isBlock = match !== null || code.includes("\n");

  if (!isBlock) {
    return (
      <code className="inline-code" {...props}>
        {children}
      </code>
    );
  }

  const language = match?.[1] ?? "plaintext";
  let highlighted: string;
  try {
    highlighted = hljs.highlight(code, { language }).value;
  } catch {
    highlighted = hljs.highlightAuto(code).value;
  }

  return <HighlightedBlock html={highlighted} language={language} code={code} />;
}

function HighlightedBlock({ html, language, code }: { html: string; language: string; code: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="group relative my-2 overflow-hidden rounded-lg bg-[#0d1117]">
      <div className="flex items-center justify-between border-b border-white/10 px-3 py-1.5 text-xs text-white/50">
        <span>{language}</span>
        <button onClick={handleCopy} className="flex items-center gap-1 hover:text-white/80">
          <i className={`fa-solid ${copied ? "fa-check" : "fa-copy"} text-[10px]`} />
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="overflow-x-auto p-3 whitespace-pre-wrap break-words">
        <code
          className={`hljs language-${language} text-sm`}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </pre>
    </div>
  );
}

function ExternalLink({ href, children, ...props }: ComponentProps<"a">) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!href) return;
    if (inTauri) {
      e.preventDefault();
      import("@tauri-apps/api/core")
        .then(({ invoke }) => invoke("open_url", { url: href }))
        .catch((err) => console.warn("open_url failed:", err));
    }
  };
  return (
    <a href={href} target="_blank" rel="noreferrer noopener" onClick={handleClick} {...props}>
      {children}
    </a>
  );
}

export function MarkdownRenderer({ content }: { content: string }) {
  return (
    <div className="prose prose-sm sm:text-[15px] dark:prose-invert max-w-none break-words [&_p]:my-1.5 [&_ul]:my-1.5 [&_ol]:my-1.5 [&_li]:my-0.5 [&_pre]:my-0 [&_pre]:bg-transparent [&_pre]:p-0 [&_blockquote]:bg-transparent">
      <Markdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{ code: CodeBlock, a: ExternalLink }}
      >
        {content}
      </Markdown>
    </div>
  );
}
